"use client";

import React from "react";
import Input from "@/components/ui/input/input";
import { Controller, useFormContext } from "react-hook-form";
import ToggleSwitch from "@/components/ui/input/toggleSwitch";
import FileUpload from "@/components/ui/input/fileUpload";
import { TokenFormData } from "@/lib/builderFormSchema";
import SelectInput from "../../input/selectInput/selectInput";

const StepTwo = () => {
  const {
    register,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useFormContext<TokenFormData>();

  const restrictPresaleEnabled = watch("restrictPresale");
  const presaleAllocation = Number(watch("presaleAllocationPercent")) || 60;
  const creatorAllocation = Number(watch("creatorAllocationPercent")) || 20;
  const totalAllocation = presaleAllocation + creatorAllocation;
  const isValidAllocation = totalAllocation === 80;

  return (
    <div className="space-y-6">
      <h2 className="text-white text-xl font-semibold mb-2">
        2- Tokenomics & Customization
      </h2>
      <p className="text-muted mb-6 text-offwhite">
        Set the economic structure of your token, including distribution, fees,
        and key market behaviors.
      </p>
      <Controller
        name="vestingSchedule"
        control={control}
        render={({ field }) => (
          <SelectInput
            label="Vesting Schedule"
            placeholder="Select vesting period (e.g., 1-5 days, Custom)"
            options={[
              { label: "1 Day", value: "1" },
              { label: "3 Days", value: "3" },
              { label: "5 Days", value: "5" },
              { label: "Custom", value: " " },
            ]}
            error={errors.vestingSchedule?.message}
            {...field}
          />
        )}
      />

      <Input
        label="Initial Market Cap"
        placeholder="Enter the initial market cap (e.g., 1000000)"
        {...register("marketCap")}
        error={errors.marketCap?.message}
        endIcon={<span className="text-base text-offwhite">Token</span>}
      />

      <div className="pt-4 flex flex-col gap-2 border-t-2 border-primaryborder">
        <label className="text-white font-semibold">Presale Settings</label>
        <p className="text-muted text-offwhite text-sm mb-2">
          Customize the presale distribution process. Restrict presale access
          and set vesting schedules to ensure fairness and transparency.
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <ToggleSwitch
            checked={watch("restrictPresale") || false}
            onChange={(e) =>
              setValue("restrictPresale", e.target.checked, {
                shouldValidate: true,
              })
            }
          />
          <span className="text-white">
            Restrict presale to verified wallets
          </span>
          <span className="text-muted text-offwhite text-sm">(0.1 SOL)</span>
        </div>
        <p className="text-muted text-offwhite text-sm mb-2">
          Only allow users from verified wallets (e.g., from BelieveIn verified
          users) to participate in the presale.
        </p>
      </div>

      {/* Only render FileUpload when restrictPresale is true */}
      {restrictPresaleEnabled && (
        <FileUpload
          fileType="csv"
          label="Upload Presale Whitelist (CSV)"
          value={watch("whitelistCsv")}
          onChange={(files) =>
            setValue("whitelistCsv", files as FileList, {
              shouldValidate: true,
            })
          }
          error={errors.whitelistCsv?.message}
        />
      )}

      <Controller
        name="presaleVestingSchedule"
        control={control}
        render={({ field }) => (
          <SelectInput
            label="Set presale vesting schedule"
            placeholder="Select vesting period (e.g., 1–5 days, Custom)"
            options={[
              { label: "1 Day", value: "1" },
              { label: "3 Days", value: "3" },
              { label: "5 Days", value: "5" },
              { label: "Custom", value: " " },
            ]}
            error={errors.presaleVestingSchedule?.message}
            {...field}
          />
        )}
      />

      <div className="pt-4 flex flex-col gap-2 border-t-2 border-primaryborder">
        <label className="text-white font-semibold">Allocation Settings</label>
        <p className="text-muted text-offwhite text-sm mb-4">
          Configure the token distribution percentages between presale, creator,
          and other allocations.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Presale Allocation (%)"
            placeholder="60"
            type="number"
            min="1"
            max="79"
            {...register("presaleAllocationPercent")}
            error={errors.presaleAllocationPercent?.message}
            endIcon={<span className="text-base text-offwhite">%</span>}
          />

          <Input
            label="Creator Allocation (%)"
            placeholder="20"
            type="number"
            min="1"
            max="79"
            {...register("creatorAllocationPercent")}
            error={errors.creatorAllocationPercent?.message}
            endIcon={<span className="text-base text-offwhite">%</span>}
          />

          <Input
            label="User Presale Limit (%)"
            placeholder="50"
            type="number"
            min="1"
            max="100"
            {...register("userPresaleLimitPercent")}
            error={errors.userPresaleLimitPercent?.message}
            endIcon={<span className="text-base text-offwhite">%</span>}
          />
        </div>

        <div
          className={`p-3 rounded-lg mt-2 ${
            isValidAllocation
              ? "bg-green-900/20 border border-green-500/30"
              : "bg-red-900/20 border border-red-500/30"
          }`}
        >
          <p
            className={`text-sm font-medium ${
              isValidAllocation ? "text-green-400" : "text-red-400"
            }`}
          >
            Total Allocation: {totalAllocation}% / 80% Required
            {isValidAllocation ? " ✓" : " ⚠️"}
          </p>
        </div>

        <div className="bg-input-bgcolor p-3 rounded-lg mt-2">
          <p className="text-xs text-offwhite">
            <strong>Note:</strong> Presale and Creator allocations must sum to
            exactly 80% (smart contract requirement). Presale allocation
            controls how much of the total supply is available during presale.
            Creator allocation is reserved for the token creator. User presale
            limit controls the maximum percentage any single user can buy during
            presale.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StepTwo;
